import { Box, Divider, Paper, Stack, Typography, useTheme } from '@mui/material';
import type { Currency } from '@weft/types';
import { Price } from 'apps/app/src/components/design/Price';
import { PercentageChip } from '../PercentageChip';
import { HomePageInvoiceStatusChart } from './HomePageInvoiceStatusChart';
import { HomePageInvoiceStatusPricingLine } from './HomePageInvoiceStatusPricingList';

type HomePageInvoiceStatusChartSectionProps = {
  currency: Currency;
  totalAmount: number;
  lastMonthAmount: number;
  openPrice: number;
  overduePrice: number;
  paidPrice: number;
  pendingPrice: number;
};

export const HomePageInvoiceStatusChartSection: React.FC<HomePageInvoiceStatusChartSectionProps> = 
({ currency, totalAmount, lastMonthAmount, openPrice, overduePrice, paidPrice, pendingPrice }) => {
  const vyTheme = useTheme();

  return (
    <Paper
      variant='outlined'
      sx={{
        flexGrow: 1,
        width: '100%',
        paddingX: 2,
        paddingY: 1,
      }}
    >
      <Stack
        direction="row"
        width="100%"
        gap={3}
      >
        <Stack
          width="100%"
          gap={1}
        >
          <Typography variant='body2'>Outstanding amount</Typography>
          <Price
            currency={currency}
            rounded
            variant='h5'
          >
            {totalAmount}
          </Price>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
          >
            <PercentageChip
              oldValue={lastMonthAmount}
              newValue={totalAmount}
            />
            <Typography variant='caption'>Monthly change</Typography>
          </Stack>
          <Stack
            direction="column"
            width="100%"
            divider={<Divider
              orientation="horizontal"
              flexItem
                     />}
          >
            <HomePageInvoiceStatusPricingLine 
              label='Paid'
              price={paidPrice}
              currency={currency}
              color={vyTheme.palette.success.light}
            />
            <HomePageInvoiceStatusPricingLine 
              label='Open'
              price={openPrice}
              currency={currency}
              color={vyTheme.palette.primary.light}
            />
            <HomePageInvoiceStatusPricingLine 
              label='Pending Payment'
              price={pendingPrice}
              currency={currency}
              color={vyTheme.palette.warning.light}
            />
            <HomePageInvoiceStatusPricingLine 
              label='Overdue'
              price={overduePrice}
              currency={currency}
              color={vyTheme.palette.error.light}
            />
          </Stack>
        </Stack>
        <Box sx={{ marginTop: 'auto' }}>
          <HomePageInvoiceStatusChart 
            openPrice={openPrice}
            overduePrice={overduePrice}
            paidPrice={paidPrice}
            pendingPrice={pendingPrice}
          />
        </Box>
      </Stack>
    </Paper>
  );
};